import React, { useEffect, useMemo, useState } from "react";
import { Plus, Loader2, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../../store/hook";
import { RootState } from "../../../../store/store";
import { fetchProcessorList, Processor } from "../../../../features/program/ProgramSlice";
import {
  fetchPrincipalProcessors,
  onboardPrincipalWithProcessor,
  clearOnboardError,
  PrincipalProcessor as PrincipalProcessorRecord,
} from "../../../../features/principal/ProcessorOnboardingSlice";
import CustomModal from "../../../common/CustomModal";

interface ProcessorOnboardingFormProps {
  principalId: string;
  principalName?: string;
  contactEmail?: string;
  contactPhone?: string;
}

/** Mirrors normalizeBrand() in programs/src/services/processorOnboarding.service.js */
function normalizeBrand(processorType: string): "YESBANK" | "ICICI_CBDC" | "ICICI" | "MYP" | "UNSUPPORTED" {
  const t = (processorType || "").toUpperCase();
  if (t.startsWith("ICICI_CBDC")) return "ICICI_CBDC";
  if (t.startsWith("ICICI")) return "ICICI";
  if (t.startsWith("YESBANK")) return "YESBANK";
  if (t === "MYP") return "MYP";
  return "UNSUPPORTED";
}

function generateYesbankRequestId(): string {
  return `REQ${Date.now()}`;
}

function generateSsTxnId(): string {
  const now = new Date();
  const datePart = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
  const sequencePart = String(Math.floor(Math.random() * 1000000)).padStart(6, "0");
  return `SS${datePart}${sequencePart}`;
}

const inputClass =
  "block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand";
const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";

type FieldValues = Record<string, string>;

const ProcessorOnboardingForm: React.FC<ProcessorOnboardingFormProps> = ({
  principalId,
  principalName = "",
  contactEmail = "",
  contactPhone = "",
}) => {
  const dispatch = useAppDispatch();
  const { processorList } = useAppSelector((state: RootState) => state.program);
  const { principalProcessors, principalProcessorsLoading, onboardLoading } = useAppSelector(
    (state: RootState) => state.processorOnboarding
  );

  const [isAdding, setIsAdding] = useState(false);
  const [selectedProcessorId, setSelectedProcessorId] = useState<string>("");
  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  const [localError, setLocalError] = useState<string>("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalErrorMessage, setModalErrorMessage] = useState<string>("");
  const [viewingProcessor, setViewingProcessor] = useState<PrincipalProcessorRecord | null>(null);

  useEffect(() => {
    dispatch(fetchProcessorList());
    if (principalId) {
      dispatch(fetchPrincipalProcessors(principalId));
    }
  }, [dispatch, principalId]);

  const onboardedProcessorIds = useMemo(
    () => new Set((principalProcessors || []).filter((p) => p.status === "onboarded").map((p) => p.processorId)),
    [principalProcessors]
  );

  const selectableProcessors = useMemo(
    () => (processorList || []).filter((p) => !onboardedProcessorIds.has(p.processorId)),
    [processorList, onboardedProcessorIds]
  );

  const selectedProcessor: Processor | undefined = useMemo(
    () => (processorList || []).find((p) => String(p.processorId) === selectedProcessorId),
    [processorList, selectedProcessorId]
  );

  const brand = selectedProcessor ? normalizeBrand(selectedProcessor.processorType) : null;

  const resetAddForm = () => {
    setIsAdding(false);
    setSelectedProcessorId("");
    setFieldValues({});
    setLocalError("");
    dispatch(clearOnboardError());
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
    dispatch(clearOnboardError());
  };

  /**
   * Re-opens the add-processor form pre-filled with the exact payload from
   * a previously failed attempt, so the user can fix the offending field
   * (e.g. a duplicate mobile number rejected by the bank) and resubmit
   * without retyping everything. The backend allows this - retrying an
   * already-failed (principal, processor) pair updates the same row; it
   * only blocks once status is "onboarded".
   */
  const handleRetryFailed = (principalProcessor: PrincipalProcessorRecord) => {
    setLocalError("");
    dispatch(clearOnboardError());
    setSelectedProcessorId(String(principalProcessor.processorId));

    const requestPayload = principalProcessor.requestPayload || {};
    const brandOfProcessor = normalizeBrand(principalProcessor.processor?.processorType || "");

    if (brandOfProcessor === "ICICI_CBDC") {
      setFieldValues({
        ...requestPayload,
        cbsCreditDebitAllowed: String(requestPayload.cbsCreditDebitAllowed ?? false),
      } as FieldValues);
    } else if (brandOfProcessor === "MYP") {
      setFieldValues({
        externalRef: requestPayload.externalRef || "",
        name: requestPayload.name || "",
        settlementAccountBankName: requestPayload.settlementAccount?.bankName || "",
        settlementAccountNumber: requestPayload.settlementAccount?.accountNumber || "",
        settlementAccountName: requestPayload.settlementAccount?.accountName || "",
      });
    } else {
      setFieldValues(requestPayload as FieldValues);
    }

    setIsAdding(true);
  };

  const handleSelectProcessor = (processorId: string) => {
    setSelectedProcessorId(processorId);
    setLocalError("");
    dispatch(clearOnboardError());

    const processor = (processorList || []).find((p) => String(p.processorId) === processorId);
    if (!processor) {
      setFieldValues({});
      return;
    }

    const detectedBrand = normalizeBrand(processor.processorType);
    if (detectedBrand === "YESBANK") {
      setFieldValues({
        requestId: generateYesbankRequestId(),
        p1: "",
        p2: principalName,
        p3: "",
        p4: contactPhone,
        p5: contactEmail,
        p6: "",
        p7: "",
        p8: "",
        p9: "E_RUPI",
        p11: "",
      });
    } else if (detectedBrand === "ICICI_CBDC") {
      setFieldValues({
        "ss-txn-id": generateSsTxnId(),
        cbsCreditDebitAllowed: "false",
        name: principalName,
        mobile: contactPhone,
        type: "PUBLIC",
        mcc: "",
        account: "",
        ifsc: "",
        allowAutomaticExpiry: "Yes",
        sendPayerNotification: "true",
        email: contactEmail,
        sponsorReferenceNumber: "",
      });
    } else if (detectedBrand === "ICICI") {
      setFieldValues({
        accountNumber: "",
        ifsc: "",
        mid: "",
        vpa: "",
        apiKey: "",
      });
    } else if (detectedBrand === "MYP") {
      setFieldValues({
        externalRef: "",
        name: principalName,
        settlementAccountBankName: "",
        settlementAccountNumber: "",
        settlementAccountName: principalName,
      });
    } else {
      setFieldValues({});
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [field]: value }));
  };

  const validate = (): string | null => {
    if (!selectedProcessor || !brand) return "Please select a processor";

    if (brand === "YESBANK") {
      const required = ["requestId", "p1", "p2", "p3", "p4", "p5", "p6", "p7", "p8"];
      for (const field of required) {
        if (!fieldValues[field]) return "Please fill all required fields";
      }
      if (!/^\d{10}$/.test(fieldValues.p4)) return "Mobile Number must be exactly 10 digits";
    } else if (brand === "ICICI_CBDC") {
      const required = ["ss-txn-id", "name", "mobile", "type", "account", "ifsc"];
      for (const field of required) {
        if (!fieldValues[field]) return "Please fill all required fields";
      }
    } else if (brand === "ICICI") {
      const required = ["accountNumber", "ifsc", "mid", "vpa", "apiKey"];
      for (const field of required) {
        if (!fieldValues[field]) return "Please fill all required fields";
      }
    } else if (brand === "MYP") {
      if (!fieldValues.externalRef || !fieldValues.name) return "Please fill all required fields";
    }
    return null;
  };

  const buildPayload = (): Record<string, any> => {
    if (brand === "YESBANK") {
      const payload: Record<string, any> = { ...fieldValues };
      if (!payload.p11) delete payload.p11;
      return payload;
    }
    if (brand === "ICICI_CBDC") {
      const payload: Record<string, any> = {
        ...fieldValues,
        cbsCreditDebitAllowed: fieldValues.cbsCreditDebitAllowed === "true",
      };
      if (!payload.email) delete payload.email;
      if (!payload.sponsorReferenceNumber) delete payload.sponsorReferenceNumber;
      return payload;
    }
    if (brand === "MYP") {
      const { externalRef, name, settlementAccountBankName, settlementAccountNumber, settlementAccountName } = fieldValues;
      const payload: Record<string, any> = { externalRef, name };
      if (settlementAccountBankName || settlementAccountNumber || settlementAccountName) {
        payload.settlementAccount = {
          bankName: settlementAccountBankName,
          accountNumber: settlementAccountNumber,
          accountName: settlementAccountName,
        };
      }
      return payload;
    }
    return { ...fieldValues };
  };

  const handleOnboardSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setLocalError(validationError);
      return;
    }
    setLocalError("");

    if (!selectedProcessor) return;

    try {
      await dispatch(
        onboardPrincipalWithProcessor({
          principalId,
          processorId: selectedProcessor.processorId,
          payload: buildPayload(),
        })
      ).unwrap();
      resetAddForm();
    } catch (error) {
      // Keep the form open with everything the user entered intact, and
      // surface the failure in a popup so it isn't missed. The inline
      // message below the fields still shows too.
      setModalErrorMessage((error as string) || "Failed to onboard principal with processor");
      setShowErrorModal(true);
    }
  };

  const renderFieldsForBrand = () => {
    if (brand === "YESBANK") {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Request ID</label>
            <input className={inputClass} value={fieldValues.requestId || ""} onChange={(e) => handleFieldChange("requestId", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Client PAN</label>
            <input className={inputClass} value={fieldValues.p1 || ""} onChange={(e) => handleFieldChange("p1", e.target.value.toUpperCase())} placeholder="ABCDE1234F" />
          </div>
          <div>
            <label className={labelClass}>Client Name (as per PAN)</label>
            <input className={inputClass} value={fieldValues.p2 || ""} onChange={(e) => handleFieldChange("p2", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Client Identifier</label>
            <input className={inputClass} value={fieldValues.p3 || ""} onChange={(e) => handleFieldChange("p3", e.target.value)} placeholder="3-12 alphanumeric" />
          </div>
          <div>
            <label className={labelClass}>Mobile Number</label>
            <input className={inputClass} value={fieldValues.p4 || ""} onChange={(e) => handleFieldChange("p4", e.target.value)} placeholder="10 digits" />
          </div>
          <div>
            <label className={labelClass}>Email ID</label>
            <input className={inputClass} value={fieldValues.p5 || ""} onChange={(e) => handleFieldChange("p5", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Source Account Number</label>
            <input className={inputClass} value={fieldValues.p6 || ""} onChange={(e) => handleFieldChange("p6", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Source Account IFSC</label>
            <input className={inputClass} value={fieldValues.p7 || ""} onChange={(e) => handleFieldChange("p7", e.target.value.toUpperCase())} placeholder="YESB0000001" />
          </div>
          <div>
            <label className={labelClass}>GST Number</label>
            <input className={inputClass} value={fieldValues.p8 || ""} onChange={(e) => handleFieldChange("p8", e.target.value.toUpperCase())} />
          </div>
          <div>
            <label className={labelClass}>MCC (required for E_RUPI)</label>
            <input className={inputClass} value={fieldValues.p11 || ""} onChange={(e) => handleFieldChange("p11", e.target.value)} placeholder="4 digits" />
          </div>
        </div>
      );
    }

    if (brand === "ICICI_CBDC") {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>ss-txn-id</label>
            <input className={inputClass} value={fieldValues["ss-txn-id"] || ""} onChange={(e) => handleFieldChange("ss-txn-id", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Name</label>
            <input className={inputClass} value={fieldValues.name || ""} onChange={(e) => handleFieldChange("name", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Mobile</label>
            <input className={inputClass} value={fieldValues.mobile || ""} onChange={(e) => handleFieldChange("mobile", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Email</label>
            <input className={inputClass} value={fieldValues.email || ""} onChange={(e) => handleFieldChange("email", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Type</label>
            <select className={inputClass} value={fieldValues.type || "PUBLIC"} onChange={(e) => handleFieldChange("type", e.target.value)}>
              <option value="PUBLIC">PUBLIC</option>
              <option value="PRIVATE">PRIVATE</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>MCC</label>
            <input className={inputClass} value={fieldValues.mcc || ""} onChange={(e) => handleFieldChange("mcc", e.target.value)} placeholder="4 digits" />
          </div>
          <div>
            <label className={labelClass}>Account Number</label>
            <input className={inputClass} value={fieldValues.account || ""} onChange={(e) => handleFieldChange("account", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>IFSC</label>
            <input className={inputClass} value={fieldValues.ifsc || ""} onChange={(e) => handleFieldChange("ifsc", e.target.value.toUpperCase())} placeholder="ICIC0000002" />
          </div>
          <div>
            <label className={labelClass}>Allow Automatic Expiry</label>
            <select className={inputClass} value={fieldValues.allowAutomaticExpiry || "Yes"} onChange={(e) => handleFieldChange("allowAutomaticExpiry", e.target.value)}>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>CBS Credit/Debit Allowed</label>
            <select className={inputClass} value={fieldValues.cbsCreditDebitAllowed || "false"} onChange={(e) => handleFieldChange("cbsCreditDebitAllowed", e.target.value)}>
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Send Payer Notification</label>
            <select className={inputClass} value={fieldValues.sendPayerNotification || "true"} onChange={(e) => handleFieldChange("sendPayerNotification", e.target.value)}>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Sponsor Reference Number</label>
            <input className={inputClass} value={fieldValues.sponsorReferenceNumber || ""} onChange={(e) => handleFieldChange("sponsorReferenceNumber", e.target.value)} />
          </div>
        </div>
      );
    }

    if (brand === "ICICI") {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Account Number</label>
            <input className={inputClass} value={fieldValues.accountNumber || ""} onChange={(e) => handleFieldChange("accountNumber", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>IFSC</label>
            <input className={inputClass} value={fieldValues.ifsc || ""} onChange={(e) => handleFieldChange("ifsc", e.target.value.toUpperCase())} />
          </div>
          <div>
            <label className={labelClass}>MID</label>
            <input className={inputClass} value={fieldValues.mid || ""} onChange={(e) => handleFieldChange("mid", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>VPA</label>
            <input className={inputClass} value={fieldValues.vpa || ""} onChange={(e) => handleFieldChange("vpa", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>API Key</label>
            <input className={inputClass} value={fieldValues.apiKey || ""} onChange={(e) => handleFieldChange("apiKey", e.target.value)} />
          </div>
        </div>
      );
    }

    if (brand === "MYP") {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>External Reference</label>
            <input className={inputClass} value={fieldValues.externalRef || ""} onChange={(e) => handleFieldChange("externalRef", e.target.value)} placeholder="CORP-001" />
          </div>
          <div>
            <label className={labelClass}>Name</label>
            <input className={inputClass} value={fieldValues.name || ""} onChange={(e) => handleFieldChange("name", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Settlement Bank Name (optional)</label>
            <input className={inputClass} value={fieldValues.settlementAccountBankName || ""} onChange={(e) => handleFieldChange("settlementAccountBankName", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Settlement Account Number (optional)</label>
            <input className={inputClass} value={fieldValues.settlementAccountNumber || ""} onChange={(e) => handleFieldChange("settlementAccountNumber", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Settlement Account Name (optional)</label>
            <input className={inputClass} value={fieldValues.settlementAccountName || ""} onChange={(e) => handleFieldChange("settlementAccountName", e.target.value)} />
          </div>
        </div>
      );
    }

    return (
      <p className="text-sm text-red-500">
        Onboarding is not supported for this processor type ({selectedProcessor?.processorType}).
      </p>
    );
  };

  /** Read-only (label, value) pairs for the payload actually sent, per brand. Mirrors renderFieldsForBrand's field set/order. */
  const getSubmittedFields = (principalProcessor: PrincipalProcessorRecord): { label: string; value: string }[] => {
    const requestPayload: Record<string, any> = principalProcessor.requestPayload || {};
    const brandOfProcessor = normalizeBrand(principalProcessor.processor?.processorType || "");
    const format = (value: any) => (value === undefined || value === null || value === "" ? "—" : String(value));

    if (brandOfProcessor === "YESBANK") {
      return [
        { label: "Request ID", value: format(requestPayload.requestId) },
        { label: "Client PAN", value: format(requestPayload.p1) },
        { label: "Client Name (as per PAN)", value: format(requestPayload.p2) },
        { label: "Client Identifier", value: format(requestPayload.p3) },
        { label: "Mobile Number", value: format(requestPayload.p4) },
        { label: "Email ID", value: format(requestPayload.p5) },
        { label: "Source Account Number", value: format(requestPayload.p6) },
        { label: "Source Account IFSC", value: format(requestPayload.p7) },
        { label: "GST Number", value: format(requestPayload.p8) },
        { label: "MCC", value: format(requestPayload.p11) },
      ];
    }
    if (brandOfProcessor === "ICICI_CBDC") {
      return [
        { label: "ss-txn-id", value: format(requestPayload["ss-txn-id"]) },
        { label: "Name", value: format(requestPayload.name) },
        { label: "Mobile", value: format(requestPayload.mobile) },
        { label: "Email", value: format(requestPayload.email) },
        { label: "Type", value: format(requestPayload.type) },
        { label: "MCC", value: format(requestPayload.mcc) },
        { label: "Account Number", value: format(requestPayload.account) },
        { label: "IFSC", value: format(requestPayload.ifsc) },
        { label: "Allow Automatic Expiry", value: format(requestPayload.allowAutomaticExpiry) },
        { label: "CBS Credit/Debit Allowed", value: format(requestPayload.cbsCreditDebitAllowed) },
        { label: "Send Payer Notification", value: format(requestPayload.sendPayerNotification) },
        { label: "Sponsor Reference Number", value: format(requestPayload.sponsorReferenceNumber) },
      ];
    }
    if (brandOfProcessor === "ICICI") {
      return [
        { label: "Account Number", value: format(requestPayload.accountNumber) },
        { label: "IFSC", value: format(requestPayload.ifsc) },
        { label: "MID", value: format(principalProcessor.identifiers?.mid) },
        { label: "VPA", value: format(principalProcessor.identifiers?.vpa) },
      ];
    }
    if (brandOfProcessor === "MYP") {
      return [
        { label: "External Reference", value: format(requestPayload.externalRef) },
        { label: "Name", value: format(requestPayload.name) },
        { label: "Settlement Bank Name", value: format(requestPayload.settlementAccount?.bankName) },
        { label: "Settlement Account Number", value: format(requestPayload.settlementAccount?.accountNumber) },
        { label: "Settlement Account Name", value: format(requestPayload.settlementAccount?.accountName) },
      ];
    }
    return [];
  };

  const renderOnboardedSummary = (principalProcessor: PrincipalProcessorRecord) => {
    const identifiers = principalProcessor.identifiers || {};
    const idFields: { label: string; value: string | null | undefined }[] = [
      { label: "Client Identifier", value: identifiers.clientIdentifier },
      { label: "Ecollect Account Number", value: identifiers.ecollectAccountNumber },
      { label: "Sponsor ID", value: identifiers.sponsorId },
      { label: "MID", value: identifiers.mid },
      { label: "VPA", value: identifiers.vpa },
      { label: "MYP Principal ID", value: identifiers.mypPrincipalId },
    ].filter((f) => f.value);

    return (
      <div className="flex flex-wrap gap-4 mt-2">
        {idFields.map((f) => (
          <div key={f.label} className="text-xs">
            <span className="text-gray-500">{f.label}: </span>
            <span className="font-mono text-gray-900">{f.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 border-[1px] border-cardborder">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">Processor Onboarding</h2>
        </div>
        {!isAdding && (
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-500 flex items-center gap-2 flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            Add Processor
          </button>
        )}
      </div>

      {/* Already onboarded / attempted processors */}
      <div className="space-y-3 mb-4">
        {principalProcessorsLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading onboarding status...
          </div>
        )}
        {(principalProcessors || []).map((principalProcessor) => {
          const isFailed = principalProcessor.status === "failed";
          const isOnboarded = principalProcessor.status === "onboarded";
          return (
            <div
              key={principalProcessor.principalProcessorId}
              onClick={
                isFailed
                  ? () => handleRetryFailed(principalProcessor)
                  : isOnboarded
                  ? () => setViewingProcessor(principalProcessor)
                  : undefined
              }
              className={`border border-gray-200 rounded-lg p-4 bg-gray-50 ${
                isFailed || isOnboarded ? "cursor-pointer hover:bg-gray-100 transition-colors" : ""
              }`}
              title={isFailed ? "Click to retry with these details" : isOnboarded ? "Click to view submitted details" : undefined}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">
                  {principalProcessor.processor?.name || `Processor #${principalProcessor.processorId}`}
                </span>
                {principalProcessor.status === "onboarded" ? (
                  <span className="flex items-center gap-1 text-xs font-medium text-green-700">
                    <CheckCircle2 className="w-4 h-4" /> Onboarded
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs font-medium text-red-600">
                    <XCircle className="w-4 h-4" /> Failed - click to retry
                  </span>
                )}
              </div>
              {principalProcessor.status === "onboarded" && renderOnboardedSummary(principalProcessor)}
              {isFailed && principalProcessor.errorMessage && (
                <p className="text-xs text-red-600 mt-2">{principalProcessor.errorMessage}</p>
              )}
            </div>
          );
        })}
        {!principalProcessorsLoading && (principalProcessors || []).length === 0 && (
          <p className="text-sm text-gray-500">No processors onboarded yet.</p>
        )}
      </div>

      {/* Add processor form */}
      {isAdding && (
        <div className="border border-gray-300 rounded-lg p-4 space-y-4">
          <div>
            <label className={labelClass}>Select Processor</label>
            <select
              className={inputClass}
              value={selectedProcessorId}
              onChange={(e) => handleSelectProcessor(e.target.value)}
            >
              <option value="">Select a processor</option>
              {selectableProcessors.map((processor) => (
                <option key={processor.processorId} value={processor.processorId}>
                  {processor.name} ({processor.processorType})
                </option>
              ))}
            </select>
          </div>

          {selectedProcessor && renderFieldsForBrand()}

          {localError && (
            <div className="text-red-600 text-sm">{localError}</div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={resetAddForm}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none transition-colors"
              disabled={onboardLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleOnboardSubmit}
              disabled={!selectedProcessor || onboardLoading}
              className="px-4 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {onboardLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Onboarding...
                </>
              ) : (
                "Onboard"
              )}
            </button>
          </div>
        </div>
      )}

      {/*
        Shown on onboarding failure. The add-processor form above stays open
        with everything the user entered untouched (handleOnboardSubmit does
        not call resetAddForm on error) so they can fix the issue and retry
        without re-entering details.
      */}
      <CustomModal
        show={showErrorModal}
        onHide={closeErrorModal}
        title="Onboarding Failed"
        width="w-[480px]"
        footer={
          <button
            type="button"
            onClick={closeErrorModal}
            className="px-4 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none transition-colors"
          >
            Close
          </button>
        }
      >
        <div className="flex items-start gap-3 py-2 w-full">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-700">{modalErrorMessage}</p>
        </div>
      </CustomModal>

      {/* Read-only view of an already-onboarded processor's submitted details - nothing here is editable. */}
      <CustomModal
        show={!!viewingProcessor}
        onHide={() => setViewingProcessor(null)}
        title={viewingProcessor?.processor?.name || "Processor Details"}
        width="w-[560px]"
        footer={
          <button
            type="button"
            onClick={() => setViewingProcessor(null)}
            className="px-4 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none transition-colors"
          >
            Close
          </button>
        }
      >
        {viewingProcessor && (
          <div className="w-full space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {getSubmittedFields(viewingProcessor).map((field) => (
                <div key={field.label}>
                  <label className={labelClass}>{field.label}</label>
                  <p className="text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    {field.value}
                  </p>
                </div>
              ))}
            </div>
            {Object.keys(viewingProcessor.identifiers || {}).length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Returned Identifiers</h4>
                {renderOnboardedSummary(viewingProcessor)}
              </div>
            )}
          </div>
        )}
      </CustomModal>
    </div>
  );
};

export default ProcessorOnboardingForm;
